import React, { useState, useEffect } from 'react';
import { RiAddLine, RiEditLine, RiDeleteBinLine, RiStickyNoteLine } from 'react-icons/ri';
import rentalNoteService, { RentalNote, RentalNoteCreate } from '../../services/rentalNoteService';
import { showAlert, showConfirm } from '../../utils/alert';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { usePermission } from '../../hooks/usePermission';
import { cn } from '../../utils/cn';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

interface RentalNotesSectionProps {
  rentalId: number;
}

const noteTypeLabels = {
  GENERAL: 'ทั่วไป',
  MAINTENANCE: 'การบำรุงรักษา',
  PAYMENT: 'การชำระเงิน',
  DELIVERY: 'การส่งมอบ',
  RETURN: 'การคืนอุปกรณ์',
  OTHER: 'อื่นๆ'
};

const noteTypeColors = {
  GENERAL: 'bg-secondary-100 text-secondary-800',
  MAINTENANCE: 'bg-warning-100 text-warning-800',
  PAYMENT: 'bg-success-100 text-success-800',
  DELIVERY: 'bg-info-100 text-info-800',
  RETURN: 'bg-accent-100 text-accent-800',
  OTHER: 'bg-warning-100 text-warning-800'
};

const RentalNotesSection: React.FC<RentalNotesSectionProps> = ({ rentalId }) => {
  const [notes, setNotes] = useState<RentalNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<RentalNoteCreate>({
    note: '',
    note_type: 'GENERAL'
  });
  const { user } = useCurrentUser();
  const { hasPermission } = usePermission();

  useEffect(() => {
    loadNotes();
  }, [rentalId]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await rentalNoteService.getRentalNotes(rentalId);
      setNotes(data);
    } catch (error) {
      console.error('Error loading notes:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดหมายเหตุได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation - use inline errors instead of showAlert
    const newErrors: Record<string, string> = {};

    if (!formData.note.trim()) {
      newErrors.note = 'กรุณากรอกหมายเหตุ';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      // Scroll to first error field and focus
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return;
    }

    try {
      setLoading(true);

      if (editingNote) {
        await rentalNoteService.updateRentalNote(editingNote, formData);
        showAlert('สำเร็จ', 'แก้ไขหมายเหตุเรียบร้อยแล้ว', 'success');
      } else {
        await rentalNoteService.createRentalNote(rentalId, formData);
        showAlert('สำเร็จ', 'เพิ่มหมายเหตุเรียบร้อยแล้ว', 'success');
      }

      // Reset form
      setFormData({ note: '', note_type: 'GENERAL' });
      setShowAddForm(false);
      setEditingNote(null);
      setErrors({});

      // Reload notes
      await loadNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถบันทึกหมายเหตุได้'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (note: RentalNote) => {
    setFormData({
      note: note.note,
      note_type: note.note_type
    });
    setEditingNote(note.id);
    setShowAddForm(true);
  };

  const handleDelete = async (noteId: number) => {
    await showConfirm(
      'ยืนยันการลบ',
      'ต้องการลบหมายเหตุนี้หรือไม่?',
      async () => {
        try {
          setLoading(true);
          await rentalNoteService.deleteRentalNote(noteId);
          showAlert('สำเร็จ', 'ลบหมายเหตุเรียบร้อยแล้ว', 'success');
          await loadNotes();
        } catch (error) {
          console.error('Error deleting note:', error);
          showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถลบหมายเหตุได้'), 'error');
        } finally {
          setLoading(false);
        }
      },
      {
        isDanger: true,
        confirmText: 'ลบ',
        cancelText: 'ยกเลิก'
      }
    );
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canEdit = (note: RentalNote) => {
    // BUG-S2167-001: PUT/DELETE ต้องมี rentals.edit ด้วย (เจ้าของโน้ตที่ถูกถอดสิทธิ์จะโดน 403)
    return hasPermission('rentals.edit') && (user?.is_superuser || note.created_by_user_id === user?.id);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
          <RiStickyNoteLine className="w-5 h-5 mr-2" />
          หมายเหตุการเช่า
        </h3>
        {(hasPermission('rentals.edit') || hasPermission('rentals.add_notes')) && (
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (showAddForm) {
                setFormData({ note: '', note_type: 'GENERAL' });
                setEditingNote(null);
                setErrors({});
              }
            }}
            className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RiAddLine className="w-4 h-4 mr-2" />
            เพิ่มหมายเหตุ
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} noValidate className="mb-6 p-4 bg-secondary-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                หมายเหตุ *
              </label>
              <textarea
                aria-label="หมายเหตุ"
                name="note"
                value={formData.note}
                onChange={(e) => {
                  setFormData({ ...formData, note: e.target.value });
                  setErrors(prev => ({ ...prev, note: '' }));
                }}
                rows={3}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.note ? 'border-danger-500' : 'border-secondary-300'
                }`}
                placeholder="กรอกหมายเหตุ..."
                aria-invalid={Boolean(errors.note)}
                aria-describedby={errors.note ? 'note-error' : undefined}
              />
              {errors.note && (
                <p id="note-error" className="mt-1 text-sm text-danger-600">{errors.note}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                ประเภท
              </label>
              <select
                aria-label="ประเภท"
                name="note_type"
                value={formData.note_type}
                onChange={(e) => setFormData({ ...formData, note_type: e.target.value as any })}
                className="block w-full px-3 py-2 border border-secondary-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(noteTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setFormData({ note: '', note_type: 'GENERAL' });
                setEditingNote(null);
                setErrors({});
              }}
              className="px-4 py-2 border border-secondary-300 rounded-md text-secondary-700 hover:bg-secondary-50"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'กำลังบันทึก...' : editingNote ? 'แก้ไข' : 'เพิ่ม'}
            </button>
          </div>
        </form>
      )}

      {/* Notes List */}
      <div className="space-y-4">
        {loading && notes.length === 0 ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-secondary-600">กำลังโหลดหมายเหตุ...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-secondary-500">
            <RiStickyNoteLine className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>ยังไม่มีหมายเหตุ</p>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="border border-secondary-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${noteTypeColors[note.note_type]}`}>
                      {noteTypeLabels[note.note_type]}
                    </span>
                    <span className="text-sm text-secondary-500">
                      โดย {note.created_by_fullname || note.created_by_username || 'ไม่ระบุ'}
                    </span>
                    <span className="text-sm text-secondary-500">
                      {formatDate(note.created_at)}
                    </span>
                  </div>
                  <p className="text-secondary-800 whitespace-pre-wrap [overflow-wrap:anywhere]">{note.note}</p>
                </div>
                {canEdit(note) && (
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(note)}
                      className="text-primary-600 hover:text-primary-800"
                      title="แก้ไข"
                    >
                      <RiEditLine className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="text-danger-600 hover:text-danger-800"
                      title="ลบ"
                    >
                      <RiDeleteBinLine className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RentalNotesSection;